"use client";

import { c as _c } from "react/compiler-runtime";
import { AuthContext } from "@/components/auth/Auth";
import { useMyStreamOptional } from "@/contexts/wave/MyStreamContext";
import type { ApiDropContextProfileContext } from "@/generated/models/ApiDropContextProfileContext";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { DropSize } from "@/helpers/waves/drop.helpers";
import { commonApiDelete, commonApiPostWithoutBodyAndResponse } from "@/services/api/common-api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useContext, useRef } from "react";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
interface BoostMutationParams {
  readonly drop: ExtendedDrop;
  readonly action: "boost" | "unboost";
}
interface UseDropBoostMutationReturn {
  readonly boostDrop: (drop: ExtendedDrop) => void;
  readonly unboostDrop: (drop: ExtendedDrop) => void;
  readonly toggleBoost: (drop: ExtendedDrop) => void;
  readonly isPending: boolean;
}
export const useDropBoostMutation = () => {
  const $ = _c(27);
  const {
    setToast,
    connectedProfile
  } = useContext(AuthContext);
  const myStreamContext = useMyStreamOptional();
  const queryClient = useQueryClient();
  const rollbackRef = useRef(null);
  const pendingDropIdRef = useRef(null);
  let t0;
  if ($[0] !== myStreamContext) {
    t0 = (drop, action) => {
      if (!myStreamContext?.applyOptimisticDropUpdate) {
        return null;
      }
      const handle = myStreamContext.applyOptimisticDropUpdate({
        waveId: drop.wave.id,
        dropId: drop.id,
        update: draft => {
          if (draft.type !== DropSize.FULL) {
            return draft;
          }
          const isCurrentlyBoosted = draft.context_profile_context?.boosted ?? false;
          const currentBoosts = draft.boosts;
          if (action === "boost" && !isCurrentlyBoosted) {
            draft.boosts = currentBoosts + 1;
          } else {
            if (action === "unboost" && isCurrentlyBoosted) {
              draft.boosts = Math.max(0, currentBoosts - 1);
            }
          }
          const baseContext = draft.context_profile_context ?? {
            rating: 0,
            min_rating: 0,
            max_rating: 0,
            reaction: null,
            boosted: false
          };
          draft.context_profile_context = {
            ...baseContext,
            boosted: action === "boost"
          };
          return draft;
        }
      });
      return handle?.rollback ?? null;
    };
    $[0] = myStreamContext;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const applyOptimisticBoost = t0;
  let t1;
  if ($[2] !== applyOptimisticBoost) {
    t1 = t2 => {
      const {
        drop: drop_1,
        action: action_1
      } = t2;
      rollbackRef.current?.();
      rollbackRef.current = applyOptimisticBoost(drop_1, action_1);
      pendingDropIdRef.current = drop_1.id;
    };
    $[2] = applyOptimisticBoost;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== queryClient) {
    t2 = async t3 => {
      const {
        drop: drop_2
      } = t3;
      rollbackRef.current = null;
      pendingDropIdRef.current = null;
      await queryClient.invalidateQueries({
        queryKey: [QueryKey.BOOSTED_DROPS, {
          waveId: drop_2.wave.id
        }]
      });
    };
    $[4] = queryClient;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== setToast) {
    t3 = (error, t4) => {
      const {
        action: action_2
      } = t4;
      console.error(`Failed to ${action_2} drop:`, error);
      rollbackRef.current?.();
      rollbackRef.current = null;
      pendingDropIdRef.current = null;
      const errorMessage = error instanceof Error ? error.message : String(error);
      setToast({
        message: action_2 === "boost" ? `Failed to boost drop: ${errorMessage}` : `Failed to remove boost: ${errorMessage}`,
        type: "error"
      });
    };
    $[6] = setToast;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t4 = {
      mutationFn: _temp,
      onMutate: t1,
      onSuccess: t2,
      onError: t3
    };
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const mutation = useMutation(t4);
  let t5;
  if ($[12] !== connectedProfile || $[13] !== mutation || $[14] !== setToast) {
    t5 = drop_3 => {
      if (!connectedProfile) {
        setToast({
          message: "Please connect your wallet to boost drops",
          type: "warning"
        });
        return;
      }
      if (pendingDropIdRef.current === drop_3.id) {
        return;
      }
      if (drop_3.id.startsWith("temp-")) {
        return;
      }
      mutation.mutate({
        drop: drop_3,
        action: "boost"
      });
    };
    $[12] = connectedProfile;
    $[13] = mutation;
    $[14] = setToast;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const boostDrop = t5;
  let t6;
  if ($[16] !== connectedProfile || $[17] !== mutation) {
    t6 = drop_4 => {
      if (!connectedProfile) {
        return;
      }
      if (pendingDropIdRef.current === drop_4.id) {
        return;
      }
      mutation.mutate({
        drop: drop_4,
        action: "unboost"
      });
    };
    $[16] = connectedProfile;
    $[17] = mutation;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const unboostDrop = t6;
  let t7;
  if ($[19] !== boostDrop || $[20] !== unboostDrop) {
    t7 = drop_5 => {
      const isBoosted = drop_5.context_profile_context?.boosted ?? false;
      if (isBoosted) {
        unboostDrop(drop_5);
      } else {
        boostDrop(drop_5);
      }
    };
    $[19] = boostDrop;
    $[20] = unboostDrop;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const toggleBoost = t7;
  let t8;
  if ($[22] !== boostDrop || $[23] !== mutation.isPending || $[24] !== toggleBoost || $[25] !== unboostDrop) {
    t8 = {
      boostDrop,
      unboostDrop,
      toggleBoost,
      isPending: mutation.isPending
    };
    $[22] = boostDrop;
    $[23] = mutation.isPending;
    $[24] = toggleBoost;
    $[25] = unboostDrop;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  return t8;
};
export default useDropBoostMutation;
async function _temp(t0) {
  const {
    drop: drop_0,
    action: action_0
  } = t0;
  const endpoint = `drops/${drop_0.id}/boosts`;
  if (action_0 === "boost") {
    await commonApiPostWithoutBodyAndResponse({
      endpoint
    });
  } else {
    await commonApiDelete({
      endpoint
    });
  }
  return {
    drop: drop_0,
    action: action_0
  };
}